import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, logout } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import Swal from "sweetalert2";
import { isMultiplaySession } from "../../utils/multiplay";

function hasPlayedBefore(user) {
  const played = Number(user?.userPlayedCount);
  return Number.isFinite(played) && played > 0;
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const save = searchParams.get("save");
  const [loginSettled, setLoginSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoginSettled(false);
      if (!save) {
        await dispatch(logout());
      }
      await dispatch(loginUser());
      if (!cancelled) {
        setLoginSettled(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, save]);

  useEffect(() => {
    if (!loginSettled || status !== "succeeded" || !user || !isAdmin) return;
    navigate("/admin");
  }, [loginSettled, status, user, isAdmin, navigate]);

  useEffect(() => {
    if (!loginSettled || status !== "succeeded" || !user?.backButtonRedirect) return;
    dispatch(setBackButtonUrl(user.backButtonRedirect));
  }, [loginSettled, status, user, dispatch]);

  useEffect(() => {
    if (!loginSettled || status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = user?.backButtonRedirect || "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [loginSettled, status, error, user]);

  useEffect(() => {
    if (!loginSettled || isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (isMultiplaySession(user)) return;
    if (!hasPlayedBefore(user)) return;

    const redirect = user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";
    Swal.fire("You have already played!", " ", "error").then(() => {
      window.location.href = redirect;
    });
  }, [loginSettled, status, user, isAdmin]);

  useEffect(() => {
    if (!loginSettled || isAdmin) return;
    if (status !== "succeeded" || !user || !adminToken) return;
    if (isMultiplaySession(user)) return;
    if (hasPlayedBefore(user)) return;

    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json();
        if (cancelled) return;
        if (data.success && data.completed) {
          const redirect = user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";
          Swal.fire("You have already played!", " ", "error").then(() => {
            window.location.href = redirect;
          });
        }
      } catch (e) {
        console.error("spotGameStatus check on login:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loginSettled, status, user, adminToken, isAdmin]);

  if (!loginSettled || status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="login-logo"
          alt="Game Logo"
        />
      )}
      {user && status === "succeeded" && (
        <div>
          <Link
            to="/game"
            className="begin-play-btn"
            style={{
              color: themeData?.text_color || "#ffffff",
              backgroundColor:
                themeData?.begin_submit_progress_bg_color ||
                themeData?.button_backgroundcolor ||
                "#6b91e8",
            }}
          >
            BEGIN PLAY
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;
